import * as React from 'react';
import * as turf from '@turf/helpers';

import { Map, Source, Layer, ViewStateChangeEvent, MapLayerMouseEvent, MapboxMap } from 'react-map-gl';

import { App } from '../../App';
import { MarkersCircleLayer, MarkersStrokeLayer } from '../Layers';
import styled from '@independent-software/typeui/styles/Theme';
import { Label } from '@independent-software/typeui/controls/Label';
import { Button } from '@independent-software/typeui/controls/Button';
import { Latitude, Longitude } from '../../controls/GIS';
import { MapControls } from '../MapControls/MapControls';

interface IProps {
  // Location initially shown op map. Can be null.
  lat?: number;
  lng?: number;
  // Initial zoom level.
  zoom: number;  
  // Handler when a location is selected.         
  onSelect: (lat: number, lng: number) => void;  
}

interface ILocation {
  lat: number;
  lng: number;
}

const PickLocationMap = (props: IProps) => {
  const map = React.useRef(null as MapboxMap);
  const [location, setLocation] = React.useState(null as ILocation);

  // If lng/lat prop a valid location?
  const isLocationValid = (): boolean => {
    if(isNaN(parseFloat(props.lat as any)) || isNaN(parseFloat(props.lng as any))) {
       return false;
    }
    return true;
  }  

  const [viewState, setViewState] = React.useState({
    longitude: isLocationValid() ? props.lng : 0,
    latitude: isLocationValid() ? props.lat : 0,
    zoom: props.zoom ?? 1,
    bearing: 0,
    pitch: 0,
    padding: { top: 0, bottom: 0, left: 0, right: 0 }    
  });  

  const handleLoad = (e: mapboxgl.MapboxEvent) => {
    map.current = e.target;
  }

  const handleMove = (e: ViewStateChangeEvent) => {
    setViewState(e.viewState);
  }

  React.useEffect(() => {
    if(map.current) {
      map.current.panTo({ lat: props.lat, lng: props.lng });
    }

  }, [props.lat, props.lng]);

  // If map is clicked, set a clicked location marker.
  const handleClick = (e: MapLayerMouseEvent) => {
    setLocation({ lat: e.lngLat.lat, lng: e.lngLat.lng });
  }  

  // If confirm button is clicked, notify subscriber.
  const handleConfirm = () => {
    props.onSelect(location.lat, location.lng);
    setLocation(null);
  }

  return (
    <Map
      {...viewState}
      mapboxAccessToken={App.config.mapboxKey}
      style={{width: '100%', height: '100%'}}
      logoPosition="bottom-left"  // Moves mapbox logo
      fadeDuration={0}            // Prevents fading in of cluster labels
      interactiveLayerIds={[]}
      mapStyle="mapbox://styles/longlineenvironment/cl8x4af5v003n14ld74f6ugwd"
      minZoom={1}
      maxZoom={20}
      doubleClickZoom={false}
      //Events
      onLoad={handleLoad}
      onMove={handleMove}      
      onClick={handleClick}
    >
      <MapControls {...viewState}/>

      {isLocationValid() && 
        <Source type="geojson" data={turf.featureCollection([turf.point([props.lng, props.lat])])}>
          <Layer {...MarkersCircleLayer} paint={{...MarkersCircleLayer.paint, "circle-opacity": location ? 0.5 : 1}}/>
          <Layer {...MarkersStrokeLayer}/>
        </Source>}

      {location && 
        <Source type="geojson" data={turf.featureCollection([turf.point([location.lng, location.lat])])}>
          <Layer {...MarkersCircleLayer} id="newmarker"/>
          <Layer {...MarkersStrokeLayer} id="newmarker-stroke"/>
        </Source>}

      <ButtonHolder>
        {location == null && !isLocationValid() && <Label basic>Click map to select location</Label>}
        {location == null && isLocationValid() && <Label basic>Click map to change location</Label>}
        {location != null && <Button secondary onClick={handleConfirm}>
          Confirm
          <Label attached="left" basic><Latitude value={location.lat}/></Label>
          <Label attached="left" basic pointing="right"><Longitude value={location.lng}/></Label>
        </Button>}
      </ButtonHolder>        
    </Map>
  );
}

const ButtonHolder = styled.div`
  position: absolute;
  z-index:  9999;
  bottom:   35px;
  right:    10px;
`

export { PickLocationMap }
